import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Users, HardHat, GraduationCap, TrendingUp, Ghost, Star, CheckCircle2, 
  Clock, Settings, FileSpreadsheet, Calculator, ArrowRight, Briefcase, UserPlus, MapPin, 
  LogIn, Loader2, BarChart3, Target, Zap, ChevronLeft, ChevronRight, Quote,
  ShieldCheck, ShieldAlert, MessageCircle,
  Users2, Scale, Map
} from 'lucide-react';

const Home = ({ language }) => { 
  
  const navigate = useNavigate();
  const reviewsContainerRef = useRef(null); 

  const [currentSlide, setCurrentSlide] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [globalRating, setGlobalRating] = useState(5); 
  const [totalReviews, setTotalReviews] = useState(0); 
  const [loadingReviews, setLoadingReviews] = useState(true);

  const GOOGLE_REVIEWS_FALLBACK = [
    { id: 1, author_name: "Ivan Muñoz", relative_time_description: "hace 5 días", rating: 5, text: "Empresa sólida, seria, responsable y con amplios conocimientos en su área. Se caracteriza por su honestidad y ayuda a resolver cualquier reto.", profile_photo_url: null, initial: "I" },
    { id: 2, author_name: "Eliana Astrid Susatama Blanco", relative_time_description: "hace 2 años", rating: 4, text: "Es una empresa que brinda buena asesoría y apoyo a nivel corporativo muy buen complemento para la empresa en Colombia 👏", profile_photo_url: null, initial: "E" },
    { id: 3, author_name: "Luis José Pulido Arango", relative_time_description: "hace 2 años", rating: 4, text: "Sigmac es una empresa comprometida con sus clientes. Ese compromiso permite que evolucione y día a día vaya mejorando en sus servicios", profile_photo_url: null, initial: "L" },
    { id: 4, author_name: "rene corrales", relative_time_description: "hace 2 años", rating: 5, text: "Empresa muy seria y que atiende con rapidez y eficiencia las necesidades referentes a la parte laboral de mi empresa. La recomiendo ampliamente", profile_photo_url: null, initial: "R" },
    { id: 5, author_name: "Daniel Villate", relative_time_description: "hace 2 años", rating: 4, text: "Es una empresa organizada y tienen buena experiencia en los temas a trabajar.", profile_photo_url: null, initial: "D" },
    { id: 6, author_name: "manuela romero", relative_time_description: "hace 2 años", rating: 5, text: "Asesoran muy bien legalmente para tomar decisiones a nivel gerencial !!", profile_photo_url: null, initial: "M" },
    { id: 7, author_name: "Catherine Franco", relative_time_description: "hace 2 años", rating: 5, text: "Empresa sería y con servicios súper importantes para cualquier organización.", profile_photo_url: null, initial: "C" },
    { id: 8, author_name: "Daniella Alejandra Arevalo", relative_time_description: "hace 2 años", rating: 5, text: "Excelente servicio y resultados tangibles", profile_photo_url: null, initial: "D" },
    { id: 9, author_name: "Margarita Jimenez", relative_time_description: "hace 2 años", rating: 5, text: "Comprometidos y preocupados por la mejora constante de sus procesos", profile_photo_url: null, initial: "M" },
    { id: 10, author_name: "Johana Moncada", relative_time_description: "hace 2 años", rating: 5, text: "Excelente grupo de apoyo!", profile_photo_url: null, initial: "J" }
  ];

  const GOOGLE_API_KEY = "AIzaSyDpxMMbU6sVTmqQtgjO6hk0jlYje_WQWFs";
  const WP_JSON_REVIEWS = "https://zaphy.com.co/wp-json/zaphy/v1/reviews";
  const PLACE_ID = "ChIJ630ejD2FP44RzMgPaCv0TSM";

  useEffect(() => {
    const fetchReviewsFromWordPress = async () => {
      try {
        setLoadingReviews(true);
        const response = await fetch(WP_JSON_REVIEWS);
        if (!response.ok) throw new Error("WordPress no respondió");
        const data = await response.json();

        if (data && (data.reviews || data.rating)) {
          setGlobalRating(data.rating || 4.8);
          setTotalReviews(data.user_ratings_total || 15);

          if (data.reviews && data.reviews.length > 0) {
            const formatted = data.reviews.map((rev, idx) => ({
              id: rev.id || `wp-review-${idx}`,
              author_name: rev.author_name || "Usuario de Zaphy",
              relative_time_description: rev.relative_time_description || "Reciente",
              rating: parseInt(rev.rating) || 5,
              text: rev.text || "",
              profile_photo_url: rev.profile_photo_url,
              initial: rev.author_name ? rev.author_name.charAt(0) : "Z"
            }));
            setReviews(formatted);
          } else {
            handleFallback();
          }
        } else {
          handleFallback();
        }
      } catch (error) {
        console.error("No se pudo obtener el JSON de WordPress. Usando respaldo local.");
        handleFallback();
      } finally {
        setLoadingReviews(false);
      }
    };

    const handleFallback = () => {
      setReviews(GOOGLE_REVIEWS_FALLBACK);
      setGlobalRating(4.8);
      setTotalReviews(15);
    };

    fetchReviewsFromWordPress();
  }, [language]);

  const scrollReviews = (direction) => {
    if (reviewsContainerRef.current) {
        const container = reviewsContainerRef.current;
        const scrollAmount = container.clientWidth * 0.8; 
        const newScrollPosition = direction === 'left' 
            ? container.scrollLeft - scrollAmount 
            : container.scrollLeft + scrollAmount;
        
        container.scrollTo({
            left: newScrollPosition,
            behavior: 'smooth'
        });
    }
  };

  const t = {
    es: {
      heroTag: 'Outsourcing de Operación Laboral Corporativa',
      heroTitle: 'Somos tu área de',
      heroSubtitle: 'Talento Humano (RRHH) y SST, lista de inmediato.',
      heroDesc: 'Asumimos la gestión de tu talento, la precisión de tu nómina y el blindaje legal de tu sistema de gestión. Libérate de la carga operativa y protege tu patrimonio.',
      btnHelp: 'Auditar Vulnerabilidad de Mi Empresa (Gratis)',
      btnQuote: 'Estructurar Mi Cotización Integral',
      
      lmTag: 'Manual de Supervivencia Corporativa',
      lmTitlePre: '¿Tu Pyme está realmente',
      lmTitleSpan: '100% protegida',
      lmTitlePost: 'ante una demanda laboral o una multa de SST?',
      lmDesc: 'Delegar la operación de tu nómina, contratos and comités de seguridad no es un lujo, es el blindaje de tu patrimonio. Realiza este test interactivo de 15 puntos críticos y descubre tus zonas de riesgo en 5 minutos.',
      lmBadge: 'Evaluación 100% Gratuita',
      lmBtn: 'Iniciar Test de Riesgo Laboral',

      waTitle: '¿Prefieres hablar directamente con un especialista?',
      waDesc: 'Despeja tus dudas sobre contratación, nómina o el SG-SST en tiempo real vía WhatsApp de forma inmediata.',
      waBtn: 'Chatear con un Asesor por WhatsApp',

      painTitle: '¿Te suena familiar?',
      painSubtitle: 'Dirigir una empresa no debería sentirse como caminar en la cuerda floja.',
      pain1Title: 'Miedo a Demandas',
      pain1Desc: 'Esa sensación constante de que un ex-empleado podría demandarte y poner en riesgo tu patrimonio.',
      pain2Title: 'Esclavos del Excel',
      pain2Desc: 'Perder horas liquidando nómina manualmente con el terror de cometer un error matemático costoso.',
      pain3Title: '¿SG-SST? ¿Qué es eso?',
      pain3Desc: 'Sabes que es obligatorio, sabes que las multas son altas, pero no tienes idea de por dónde empezar.',
      servicesTag: 'Nuestra Solución',
      servicesTitle: 'Kit de Supervivencia',
      servicesSpan: 'Empresarial',
      servicesDesc: 'Todo lo que necesitas para operar legalmente y crecer sin frenos.',
      srvEstructura: 'Estructura Organizacional',
      srvEstructuraDesc: 'La base del crecimiento',
      srvEstructuraFull: 'Diseñamos el esqueleto de tu empresa para que cada rol esté claro y la operación fluya.',
      srvSST: 'Seguridad y Salud (SST)',
      srvSSTDesc: 'Cumplimiento sin estrés',
      srvSSTFull: 'Implementamos el sistema de gestión obligatorio y evitamos multas millonarias.',
      srvRRHH: 'Outsourcing RR.HH.',
      srvRRHHDesc: 'Tu departamento externo',
      srvRRHHFull: 'Manejamos el bienestar, clima laboral y procesos disciplinarios por ti.',
      srvSeleccion: 'Selección de Personal',
      srvSeleccionDesc: 'El talento correcto',
      srvSeleccionFull: 'Usamos pruebas psicotécnicas y entrevistas profundas para encontrar al ideal.',
      srvIndependientes: 'Servicios Independientes',
      srvIndependientesDesc: 'A tu medida',
      srvIndependientesFull: 'Afiliaciones, asesorías o capacitaciones puntuales sin ataduras mensuales.',
      srvNomina: 'Outsourcing Nómina',
      srvNominaDesc: 'Precisión matemática',
      srvNominaFull: 'Cálculos exactos, seguridad social al día y cero errores ante la UGPP.',
      whyTitle: '¿Por qué confiar en',
      why1Title: 'Hablamos tu idioma',
      why1Desc: "Nada de 'latín jurídico'. Te explicamos todo claro y sin rodeos.",
      why2Title: 'Expertos Reales',
      why2Desc: 'Detrás de la mascota hay Abogados, Psicólogos y Especialistas en SST senior.',
      why3Title: 'Ahorro Garantizado',
      why3Desc: 'Mucho más económico que tener un departamento interno de RR.HH. y Seguridad.',
      dashTitle: 'Dashboard Zaphy',
      dash1: '100% Cumplimiento',
      dash1Sub: 'Legal, RRHH y SST',
      dash2: '+1000 Contratos Blindados',
      dash2Sub: 'Gestión sin riesgos',
      bukTitle: 'Acceso a tu Portal del Colaborador',
      bukDesc: 'Tu espacio personal para consultar desprendibles, certificados y solicitar vacaciones. Todo al alcance de un clic gracias a nuestra alianza tecnológica.',
      bukLabel: 'Tecnología aliada:',
      bukBtn: 'Ingresar a mi Plataforma',
      testTitle: 'Lo que dicen nuestros clientes',
      testSub: 'en Google Reviews'
    },
    en: {
      heroTag: 'Corporate Personnel Operations Outsourcing',
      heroTitle: 'We become your external',
      heroSubtitle: 'HR & SST Department instantly.',
      heroDesc: 'We take over your talent management, payroll precision, and the legal compliance of your workplace safety system. Break free from operational burdens.',
      btnHelp: 'Audit My Company Risks (Free)',
      btnQuote: 'Build My Custom Estimate',
      
      lmTag: 'Corporate Survival Guide',
      lmTitlePre: 'Is your company truly',
      lmTitleSpan: '100% protected',
      lmTitlePost: 'against labor lawsuits or SST safety fines?',
      lmDesc: 'Delegating your payroll, employment contracts, and safety committees operations is not a luxury, it shields your business assets. Take this interactive 15-point quiz to discover your risk levels in 5 minutes.',
      lmBadge: '100% Free Assessment',
      lmBtn: 'Start Labor Risk Test',

      waTitle: 'Would you rather speak with a specialist directly?',
      waDesc: 'Clear up any questions about legal contracts, corporate payroll, or SST compliance in real-time instantly via WhatsApp.',
      waBtn: 'Chat with an Advisor on WhatsApp',

      painTitle: 'Sound familiar?',
      painSubtitle: "Running a business shouldn't feel like walking on a tightrope.",
      pain1Title: 'Fear of Lawsuits',
      pain1Desc: 'That constant feeling that an ex-employee could sue you and put your assets at risk.',
      pain2Title: 'Excel Slaves',
      pain2Desc: 'Wasting hours liquidating payroll manually with the terror of making a costly math error.',
      pain3Title: 'SG-SST? What is that?',
      pain3Desc: 'You know it\'s mandatory, you know the fines are high, but you have no idea where to start.',
      servicesTag: 'Our Solution',
      servicesTitle: 'Survival Kit For',
      servicesSpan: 'Businesses',
      servicesDesc: 'Everything you need to operate legally and grow without brakes.',
      srvEstructura: 'Organizational Structure',
      srvEstructuraDesc: 'The basis for growth',
      srvEstructuraFull: "We design your company's skeleton so every role is clear and operations flow.",
      srvSST: 'Health & Safety (SST)',
      srvSSTDesc: 'Compliance without stress',
      srvSSTFull: 'We implement the mandatory management system and avoid millionaire fines.',
      srvRRHH: 'HR Outsourcing',
      srvRRHHDesc: 'Your external department',
      srvRRHHFull: 'We manage wellness, work climate, and disciplinary processes for you.',
      srvSeleccion: 'Recruitment',
      srvSeleccionDesc: 'The right talent',
      srvSeleccionFull: 'We use psychotechnical tests and in-depth interviews to find the ideal candidate.',
      srvIndependientes: 'Independent Services',
      srvIndependientesDesc: 'Tailored to you',
      srvIndependientesFull: 'Affiliations, advice, or specific training without monthly ties.',
      srvNomina: 'Payroll Outsourcing',
      srvNominaDesc: 'Payroll precision',
      srvNominaFull: 'Exact calculations, up-to-date social security, and zero errors before the UGPP.',
      whyTitle: 'Why trust',
      why1Title: 'We speak your language',
      why1Desc: "No 'legal latin'. We explain everything clearly and bluntly.",
      why2Title: 'Real Experts',
      why2Desc: 'Behind the mascot are senior Lawyers, Psychologists, and SST Specialists.',
      why3Title: 'Guaranteed Savings',
      why3Desc: 'Much cheaper than having an in-house HR and Safety department.',
      dashTitle: 'Zaphy Dashboard',
      dash1: '100% Compliance',
      dash1Sub: 'Legal, HR & SST',
      dash2: '+1000 Shielded Contracts',
      dash2Sub: 'Risk-free management',
      bukTitle: 'Access your Collaborator Portal',
      bukDesc: 'Your personal space to check pay stubs, certificates, and request vacations. Everything just a click away thanks to our tech partnership.',
      bukLabel: 'Powered by:',
      bukBtn: 'Login to my Platform',
      testTitle: 'What our clients say',
      testSub: 'on Google Reviews'
    }
  };

  const text = t[language] || t.es;

  const HERO_SLIDES_DATA = [
      {
        id: 1,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/mascotazaphy.png",
        alt: "Mascota Zaphy",
        imgClass: "object-contain p-4 scale-100",
        card1: { 
            icon: Users, color: "purple", barWidth: "95%", valueColor: "text-green-500",
            texts: { es: { title: "RR.HH.", label: "Clima Laboral", value: "Optimizado" }, en: { title: "HR", label: "Work Climate", value: "Optimized" } }
        },
        /* 🛑 EL ERROR EN TU CÓDIGO (Línea 171 aprox): */
        card2: { 
            icon: TrendingUp, color: "yellow", barWidth: "95%", valueColor: "text-yellow-600",
            texts: { es: { title: "Gestión", label: "Productividad", value: "95%" }, en: { title: "Management", label: "Productivity", value: "95%" } }
        }
      },
      {
        id: 2,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/IMG-20250617-WA0123.jpg",
        alt: "Capacitaciones de personal",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: GraduationCap, color: "blue", barWidth: "98%", valueColor: "text-blue-600",
            texts: { es: { title: "Formación", label: "Personal", value: "98%" }, en: { title: "Training", label: "Staff", value: "98%" } }
        },
        card2: { 
            icon: TrendingUp, color: "yellow", barWidth: "95%", valueColor: "text-yellow-600",
            texts: { es: { title: "Gestión", label: "Productividad", value: "95%" }, en: { title: "Management", label: "Productivity", value: "95%" } }
        }
      },
      {
        id: 3,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/20220726_100936-scaled.jpg",
        alt: "Clientes Felices",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: Star, color: "orange", barWidth: "100%", valueColor: "text-orange-500",
            texts: { es: { title: "Calidad", label: "Satisfacción", value: "Garantizada" }, en: { title: "Quality", label: "Satisfaction", value: "Guaranteed" } }
        },
        card2: { 
            icon: Shield, color: "green", barWidth: "100%", valueColor: "text-green-600",
            texts: { es: { title: "Legal", label: "Riesgos", value: "Blindados" }, en: { title: "Legal", label: "Risks", value: "Shielded" } }
        }
      },
      {
        id: 4,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/home-5.jpg",
        alt: "Bienestar y Dinámicas de Equipo",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: Users, color: "teal", barWidth: "100%", valueColor: "text-teal-600",
            texts: { es: { title: "Bienestar", label: "Eventos", value: "Impacto" }, en: { title: "Wellness", label: "Events", value: "Impact" } }
        },
        card2: { 
            icon: Clock, color: "red", barWidth: "100%", valueColor: "text-red-600",
            texts: { es: { title: "Tiempos", label: "Respuesta", value: "Inmediata" }, en: { title: "Timing", label: "Response", value: "Immediate" } }
        }
      },
      {
        id: 5,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/hpme-6.jpg",
        alt: "Actividades de SST en oficina",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: HardHat, color: "yellow", barWidth: "100%", valueColor: "text-emerald-600",
            texts: { es: { title: "SST", label: "Accidentes", value: "0%" }, en: { title: "SST", label: "Accidents", value: "0%" } }
        },
        card2: { 
            icon: FileSpreadsheet, color: "blue", barWidth: "100%", valueColor: "text-blue-600",
            texts: { es: { title: "Reportes", label: "Entrega", value: "A tiempo" }, en: { title: "Reports", label: "Delivery", value: "On time" } }
        }
      },
      {
        id: 6,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/IMG_20230301_073128.jpg",
        alt: "Cultura Organizacional al aire libre",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: Ghost, color: "orange", barWidth: "100%", valueColor: "text-orange-700",
            texts: { es: { title: "Sin Miedos", label: "Legalidad", value: "Total" }, en: { title: "No Fears", label: "Legality", value: "Total" } }
        },
        card2: { 
            icon: Users, color: "cyan", barWidth: "100%", valueColor: "text-cyan-600",
            texts: { es: { title: "Cultura", label: "Clima", value: "Positivo" }, en: { title: "Culture", label: "Climate", value: "Positive" } }
        }
      },
      {
        id: 7,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/IMG-20250912-WA0023.jpg",
        alt: "Dinámica grupal de integración",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: Target, color: "pink", barWidth: "96%", valueColor: "text-pink-600",
            texts: { es: { title: "Equipo", label: "Alineación", value: "96%" }, en: { title: "Team", label: "Alignment", value: "96%" } }
        },
        card2: { 
            icon: CheckCircle2, color: "green", barWidth: "100%", valueColor: "text-green-600",
            texts: { es: { title: "Procesos", label: "Ejecución", value: "Eficiente" }, en: { title: "Processes", label: "Execution", value: "Efficient" } }
        }
      },
      {
        id: 8,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/home4.jpeg",
        alt: "Asesorías Corporativas",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: ShieldCheck, color: "purple", barWidth: "100%", valueColor: "text-purple-600",
            texts: { es: { title: "Protección", label: "Patrimonio", value: "Blindado" }, en: { title: "Protection", label: "Assets", value: "Shielded" } }
        },
        card2: { 
            icon: Briefcase, color: "indigo", barWidth: "95%", valueColor: "text-indigo-600",
            texts: { es: { title: "Estructura", label: "Crecimiento", value: "Sólido" }, en: { title: "Structure", label: "Growth", value: "Solid" } }
        }
      },
      {
        id: 9,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/home-3.jpg",
        alt: "Capacitaciones de seguridad",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: HardHat, color: "yellow", barWidth: "98%", valueColor: "text-yellow-700",
            texts: { es: { title: "Prevención", label: "Cultura SST", value: "98%" }, en: { title: "Prevention", label: "SST Culture", value: "98%" } }
        },
        card2: { 
            icon: Zap, color: "cyan", barWidth: "100%", valueColor: "text-cyan-600",
            texts: { es: { title: "Operación", label: "Carga Operativa", value: "100%" }, en: { title: "Operation", label: "Operational Load", value: "Zero" } }
        }
      },
      {
        id: 10,
        image: "https://zaphy.com.co/wp-content/uploads/2026/01/estructura-organizacional-2.jpg",
        alt: "Diseño organizacional de Recursos Humanos",
        imgClass: "object-cover object-center scale-110",
        card1: { 
            icon: BarChart3, color: "blue", barWidth: "92%", valueColor: "text-blue-600",
            texts: { es: { title: "Métricas", label: "Desempeño", value: "Subiendo" }, en: { title: "Metrics", label: "Performance", value: "Rising" } }
        },
        card2: { 
            icon: UserPlus, color: "teal", barWidth: "100%", valueColor: "text-teal-600",
            texts: { es: { title: "Selección", label: "Talento", value: "Idóneo" }, en: { title: "Recruitment", label: "Talent", value: "Ideal" } }
        }
      }
  ];

  const getCardStyles = (color) => {
      const styles = {
          purple: { bg: 'bg-purple-100', text: 'text-purple-600', title: 'text-purple-800', bar: 'bg-purple-500' },
          cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', title: 'text-cyan-800', bar: 'bg-cyan-500' },
          blue: { bg: 'bg-blue-100', text: 'text-blue-600', title: 'text-blue-800', bar: 'bg-blue-600' },
          yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', title: 'text-yellow-800', bar: 'bg-yellow-500' },
          orange: { bg: 'bg-orange-100', text: 'text-orange-600', title: 'text-orange-800', bar: 'bg-orange-500' },
          green: { bg: 'bg-green-100', text: 'text-green-600', title: 'text-green-800', bar: 'bg-green-600' },
          indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', title: 'text-indigo-800', bar: 'bg-indigo-600' },
          pink: { bg: 'bg-pink-100', text: 'text-pink-600', title: 'text-pink-800', bar: 'bg-pink-500' },
          teal: { bg: 'bg-teal-100', text: 'text-teal-600', title: 'text-teal-800', bar: 'bg-teal-500' },
          red: { bg: 'bg-red-100', text: 'text-red-600', title: 'text-red-800', bar: 'bg-red-500' },
          gray: { bg: 'bg-gray-100', text: 'text-gray-600', title: 'text-gray-800', bar: 'bg-gray-600' },
      };
      return styles[color] || styles.purple;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES_DATA.length);
    }, 5000); 
    return () => clearInterval(interval);
  }, [HERO_SLIDES_DATA.length]);

  const slide = HERO_SLIDES_DATA[currentSlide];
  const Icon1 = slide?.card1?.icon;
  const Icon2 = slide?.card2?.icon;
  
  const style1 = getCardStyles(slide.card1.color);
  const style2 = getCardStyles(slide.card2.color);

  const c1Text = slide.card1.texts[language] || slide.card1.texts.es;
  const c2Text = slide.card2.texts[language] || slide.card2.texts.es;

  return (
    <div className="animate-[fadeIn_0.5s_ease-out]">
      
      {/* --- SECCIÓN 1: HERO --- */}
      <section className="relative pt-36 pb-20 sm:pt-48 sm:pb-32 overflow-hidden bg-[#F9FAFB]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[100px] -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cyan-600/10 rounded-full blur-[100px] -ml-32 -mb-32"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Texto Izquierda */}
            <div className="text-center lg:text-left space-y-8 animate-[slideRight_0.8s_ease-out]">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-800 font-bold text-sm border border-purple-100 shadow-md transform hover:scale-105 transition-transform">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                </span>
                {text.heroTag}
              </div>
              
              <h1 className="text-5xl sm:text-6xl font-extrabold text-gray-900 leading-[1.15] tracking-tight font-[Poppins]">
                {text.heroTitle} <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-700 via-purple-600 to-cyan-500">
                  {text.heroSubtitle}
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium font-[Poppins]">
                {text.heroDesc}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <button onClick={() => navigate('/contacto')} className="bg-purple-800 hover:bg-purple-900 text-white text-base px-6 py-4 rounded-2xl font-bold shadow-xl hover:shadow-purple-500/40 hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group font-[Poppins]">
                  {text.btnHelp} <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform"/>
                </button>
                <button onClick={() => navigate('/cotizador')} className="bg-white border-2 border-cyan-400 text-cyan-700 px-6 py-4 rounded-2xl font-bold transition-all hover:shadow-lg hover:bg-cyan-50 flex items-center justify-center gap-2 font-[Poppins]">
                  <Calculator size={20} />
                  {text.btnQuote}
                </button>
              </div>
            </div>

            {/* Imagen/Mascota Derecha */}
            <div className="w-full max-w-md sm:max-w-none mt-12 lg:mt-0 relative perspective-1000 animate-[fadeIn_1s_ease-out] mx-auto lg:mx-0">
               <div className="relative aspect-square flex items-center justify-center transform hover:translate-y-6 transition-transform duration-700 preserve-3d">
                  <div className="absolute inset-0 bg-gradient-to-tr from-purple-200/50 to-cyan-200/50 rounded-full opacity-60 blur-3xl animate-pulse"></div>
                  <div className="absolute w-[90%] h-[90%] border-[8px] border-dashed border-purple-400/60 rounded-full animate-[spin_25s_linear_infinite]"></div>
                  <div className="absolute w-[85%] h-[85%] border-[6px] border-dashed border-cyan-400/60 rounded-full animate-[spin_20s_linear_infinite_reverse]"></div>

                  {/* Contenedor del Óvalo/Círculo Principal - CORREGIDO CON TOTAL RELLENO */}
                  <div className="relative w-[78%] h-[78%] bg-white rounded-full shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white/50 overflow-hidden flex items-center justify-center aspect-square">
                      <div key={slide.id} className="absolute inset-0 w-full h-full animate-[fadeIn_0.8s_ease-out]">
                          <img 
                              src={slide.image} 
                              alt={slide.alt} 
                              className={`absolute inset-0 w-full h-full ${slide.imgClass} transition-transform duration-500`} 
                              onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/400x400.png?text=Zaphy'; }}
                          />
                      </div>
                  </div>
                  
                  {/* Tarjetas Flotantes */}
                  <div key={`c1-${slide.id}`} className="absolute top-0 right-0 bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-xl animate-[float_6s_ease-in-out_infinite] border border-purple-100 max-w-[180px] transition-all duration-500">
                      <div className="flex items-center gap-3 mb-2">
                          <div className={`${style1.bg} p-2 rounded-lg ${style1.text}`}>{Icon1 && <Icon1 size={20} />}</div>
                          <span className={`text-xs font-bold ${style1.title} uppercase tracking-wider font-[Poppins]`}>{c1Text.title}</span>
                      </div>
                      <div className="space-y-2">
                         <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                             <div className={`h-full ${style1.bar}`} style={{ width: slide.card1.barWidth }}></div>
                         </div>
                         <div className="flex justify-between text-[10px] text-gray-500 font-medium font-[Poppins] gap-2">
                             <span>{c1Text.label}</span>
                             <span className={slide.card1.valueColor}>{c1Text.value}</span>
                         </div>
                      </div>
                  </div>

                  <div key={`c2-${slide.id}`} className="absolute bottom-10 left-0 bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-xl animate-[float_5s_ease-in-out_infinite_reverse] border border-cyan-100 max-w-[180px] transition-all duration-500">
                      <div className="flex items-center gap-3 mb-2">
                          <div className={`${style2.bg} p-2 rounded-lg ${style2.text}`}>{Icon2 && <Icon2 size={20} />}</div>
                          <span className={`text-xs font-bold ${style2.title} uppercase tracking-wider font-[Poppins]`}>{c2Text.title}</span>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                         <CheckCircle2 size={12} className={slide.card2.valueColor}/>
                         <span className={`text-[10px] font-bold ${slide.card2.valueColor} font-[Poppins]`}>{c2Text.value}</span>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SECCIÓN 2: LEAD MAGNET --- */}
      <section className="relative my-12 mx-4 sm:mx-6 lg:mx-8 overflow-hidden rounded-[32px] bg-[#0F172A] border border-white/10 shadow-2xl">
        <div className="absolute -top-12 -right-12 text-purple-500/10 pointer-events-none animate-[spin_50s_linear_infinite]">
          <Settings size={200} strokeWidth={1} fill="currentColor" />
        </div>
        <div className="absolute -bottom-10 -left-10 text-cyan-400/10 pointer-events-none animate-[spin_30s_linear_infinite_reverse]">
          <Settings size={160} strokeWidth={1} fill="currentColor" />
        </div>
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-72 h-72 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-4 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 px-3 py-1.5 rounded-full">
                <Zap size={14} className="text-cyan-400 animate-pulse" />
                <span className="text-xs font-bold tracking-wider text-cyan-300 uppercase font-[Poppins]">
                  {text.lmTag}
                </span>
              </div>
              <h3 className="text-2xl sm:text-4xl font-extrabold font-[Poppins] leading-tight text-white">
                {text.lmTitlePre} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">{text.lmTitleSpan}</span> {text.lmTitlePost}
              </h3>
              <p className="text-gray-300 font-[Poppins] text-sm sm:text-base leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {text.lmDesc}
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-center gap-6 bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-2xl w-full">
              <div className="relative flex-shrink-0 w-20 h-20 bg-gradient-to-tr from-purple-600 to-cyan-500 rounded-2xl p-0.5 shadow-lg overflow-hidden group">
                <div className="relative w-full h-full bg-slate-950 rounded-2xl flex items-center justify-center p-1 overflow-hidden">
                  <img 
                    src="https://zaphy.com.co/wp-content/uploads/2026/01/mascotazaphy.png" 
                    alt="Zaphy" 
                    className="w-full h-full object-contain relative z-10 transform group-hover:scale-110 transition-transform duration-300"
                  />
                  <Settings size={48} className="absolute text-purple-500/20 animate-[spin_10s_linear_infinite]" />
                </div>
              </div>

              <div className="flex-grow space-y-3 text-center sm:text-left lg:text-center xl:text-left w-full">
                <div className="flex items-center justify-center sm:justify-start lg:justify-center xl:justify-start gap-2 text-emerald-400">
                  <ShieldCheck size={16} />
                  <span className="text-xs font-bold font-[Poppins] tracking-wide uppercase">{text.lmBadge}</span>
                </div>
                
                <button 
                  onClick={() => navigate('/test-vulnerabilidad')}
                  className="group bg-gradient-to-r from-cyan-400 to-teal-400 hover:from-cyan-500 hover:to-teal-500 text-slate-950 font-black text-sm px-5 py-3.5 rounded-xl shadow-md hover:shadow-cyan-400/20 hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 font-[Poppins] w-full"
                >
                  {text.lmBtn}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>
      {/* --- SECCIÓN DE ESTADÍSTICAS Y PRUEBA SOCIAL (NEUROMARKETING DE CREDIBILIDAD) --- */}
      <section className="bg-gray-50 py-16 border-t border-b border-gray-100 font-[Poppins]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-bold text-xs uppercase tracking-wider block">
              {language === 'es' ? 'Respaldo Institucional' : 'Institutional Backing'}
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-2">
              {language === 'es' 
                ? 'La tranquilidad de tu PyME respaldada por números reales' 
                : 'Your Business Peace of Mind Backed by Real Numbers'}
            </h2>
          </div>

          {/* GRID DE CONTADORES */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* CARD 1: EMPRESAS BLINDADAS */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center group hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <ShieldCheck size={24} />
              </div>
              <span className="text-3xl md:text-4xl font-black text-gray-950 tracking-tight">
                +50
              </span>
              <h3 className="font-bold text-sm text-gray-900 mt-2">
                {language === 'es' ? 'Empresas Blindadas' : 'Shielded Companies'}
              </h3>
              <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                {language === 'es' 
                  ? 'PyMEs que operan con cero sanciones ante entes de control.' 
                  : 'Businesses operating with zero sanctions before control entities.'}
              </p>
            </div>

            {/* CARD 2: COLABORADORES GESTIONADOS */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center group hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-cyan-50 text-cyan-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Users2 size={24} />
              </div>
              <span className="text-3xl md:text-4xl font-black text-gray-950 tracking-tight">
                +3.200
              </span>
              <h3 className="font-bold text-sm text-gray-900 mt-2">
                {language === 'es' ? 'Colaboradores Gestionados' : 'Managed Employees'}
              </h3>
              <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                {language === 'es' 
                  ? 'Vidas protegidas en SST y nóminas liquidadas con precisión.' 
                  : 'Lives protected in OHS and payrolls processed with precision.'}
              </p>
            </div>

            {/* CARD 3: REQUERIMIENTOS EXITOSOS */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center group hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Scale size={24} />
              </div>
              <span className="text-3xl md:text-4xl font-black text-gray-950 tracking-tight">
                100%
              </span>
              <h3 className="font-bold text-sm text-gray-900 mt-2">
                {language === 'es' ? 'Defensa Legal Exitosa' : 'Successful Legal Defense'}
              </h3>
              <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                {language === 'es' 
                  ? 'De procesos disciplinarios y auditorías resueltas a favor.' 
                  : 'Of disciplinary hearings and audits successfully resolved.'}
              </p>
            </div>

            {/* CARD 4: CIUDADES CON COBERTURA */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center group hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Map size={24} /> {/* <-- Cambiado Activity por Map para máxima coherencia visual */}
              </div>
              <span className="text-3xl md:text-4xl font-black text-gray-950 tracking-tight">
                3
              </span>
              <h3 className="font-bold text-sm text-gray-900 mt-2">
                {language === 'es' ? 'Territorio Zaphy' : 'Zaphy Coverage Area'}
              </h3>
              <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                {language === 'es' 
                  ? 'Estamos en Bogotá, Medellín y Cali, el Valle del Cauca.' 
                  : 'Robust operation in Bogota, Medellin, and Cali, Valle del Cauca.'}
              </p>
            </div>

          </div>
        </div>
      </section>
      {/* --- SECCIÓN 3: DOLORES --- */}
      <section className="py-24 bg-white relative z-20 rounded-t-[40px] -mt-10 shadow-inner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-extrabold text-gray-900 mb-4 font-[Poppins]">{text.painTitle}</h2>
              <p className="text-xl text-gray-500 max-w-2xl mx-auto font-[Poppins]">{text.painSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="bg-gray-50 p-10 rounded-3xl hover:bg-purple-50 transition-colors duration-300 group border border-transparent hover:border-purple-200 hover:-translate-y-2 transform">
                <div className="w-16 h-16 bg-white shadow-md text-red-500 rounded-2xl flex items-center justify-center mb-8"><Ghost size={32} /></div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-[Poppins]">{text.pain1Title}</h3>
                <p className="text-gray-600 font-[Poppins]">{text.pain1Desc}</p>
              </div>
               <div className="bg-gray-50 p-10 rounded-3xl hover:bg-cyan-50 transition-colors duration-300 group border border-transparent hover:border-cyan-200 hover:-translate-y-2 transform">
                <div className="w-16 h-16 bg-white shadow-md text-cyan-600 rounded-2xl flex items-center justify-center mb-8"><Clock size={32} /></div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-[Poppins]">{text.pain2Title}</h3>
                <p className="text-gray-600 font-[Poppins]">{text.pain2Desc}</p>
              </div>
               <div className="bg-gray-50 p-10 rounded-3xl hover:bg-yellow-50 transition-colors duration-300 group border border-transparent hover:border-yellow-200 hover:-translate-y-2 transform">
                <div className="w-16 h-16 bg-white shadow-md text-yellow-500 rounded-2xl flex items-center justify-center mb-8"><HardHat size={32} /></div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-[Poppins]">{text.pain3Title}</h3>
                <p className="text-gray-600 font-[Poppins]">{text.pain3Desc}</p>
              </div>
            </div>
          </div>
      </section>

      {/* --- SECCIÓN 4: SERVICIOS --- */}
      <section className="py-24 bg-[#0F172A] text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-20">
               <span className="text-cyan-400 font-bold tracking-[0.2em] uppercase text-xs mb-3 block font-[Poppins]">{text.servicesTag}</span>
               <h2 className="text-4xl sm:text-5xl font-extrabold mt-2 tracking-tight font-[Poppins]">{text.servicesTitle} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">{text.servicesSpan}</span></h2>
               <p className="text-gray-400 mt-4 max-w-2xl mx-auto font-[Poppins] text-lg">{text.servicesDesc}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#8B0C3C] overflow-hidden cursor-pointer" onClick={() => navigate('/estructura-organizacional')}>
                 <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-[#8B0C3C] to-transparent"></div>
                 <div className="relative z-10">
                    <div className="w-14 h-14 bg-[#8B0C3C] text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg"><TrendingUp size={28} /></div>
                    <h3 className="text-xl font-bold mb-2 text-white font-[Poppins] group-hover:text-[#ff4d94] transition-colors">{text.srvEstructura}</h3>
                    <p className="text-[#ff80b3] text-sm font-medium mb-4">{text.srvEstructuraDesc}</p>
                    <p className="text-gray-300 text-sm leading-relaxed font-[Poppins]">{text.srvEstructuraFull}</p>
                 </div>
              </div>

              <div className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#E3FF00] overflow-hidden cursor-pointer" onClick={() => navigate('/sst')}>
                 <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-[#E3FF00] to-transparent"></div>
                 <div className="relative z-10">
                    <div className="w-14 h-14 bg-[#E3FF00] text-black rounded-2xl flex items-center justify-center mb-6 shadow-lg"><HardHat size={28} /></div>
                    <h3 className="text-xl font-bold mb-2 text-white font-[Poppins] group-hover:text-[#E3FF00] transition-colors">{text.srvSST}</h3>
                    <p className="text-[#f7ffb8] text-sm font-medium mb-4">{text.srvSSTDesc}</p>
                    <p className="text-gray-300 text-sm leading-relaxed font-[Poppins]">{text.srvSSTFull}</p>
                 </div>
              </div>

              <div className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-cyan-500 overflow-hidden cursor-pointer" onClick={() => navigate('/recursos-humanos')}>
                 <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-cyan-500 to-transparent"></div>
                 <div className="relative z-10">
                    <div className="w-14 h-14 bg-cyan-500 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg"><Users size={28} /></div>
                    <h3 className="text-xl font-bold mb-2 text-white font-[Poppins] group-hover:text-cyan-300 transition-colors">{text.srvRRHH}</h3>
                    <p className="text-cyan-200 text-sm font-medium mb-4">{text.srvRRHHDesc}</p>
                    <p className="text-gray-300 text-sm leading-relaxed font-[Poppins]">{text.srvRRHHFull}</p>
                 </div>
              </div>

              <div className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-pink-500 overflow-hidden cursor-pointer" onClick={() => navigate('/seleccion-personal')}>
                 <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-pink-500 to-transparent"></div>
                 <div className="relative z-10">
                    <div className="w-14 h-14 bg-pink-500 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg"><UserPlus size={28} /></div>
                    <h3 className="text-xl font-bold mb-2 text-white font-[Poppins] group-hover:text-pink-300 transition-colors">{text.srvSeleccion}</h3>
                    <p className="text-pink-200 text-sm font-medium mb-4">{text.srvSeleccionDesc}</p>
                    <p className="text-gray-300 text-sm leading-relaxed font-[Poppins]">{text.srvSeleccionFull}</p>
                 </div>
              </div>

              <div className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-indigo-500 overflow-hidden cursor-pointer" onClick={() => navigate('/servicios-independientes')}>
                 <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-indigo-500 to-transparent"></div>
                 <div className="relative z-10">
                    <div className="w-14 h-14 bg-indigo-500 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg"><Settings size={28} /></div>
                    <h3 className="text-xl font-bold mb-2 text-white font-[Poppins] group-hover:text-indigo-300 transition-colors">{text.srvIndependientes}</h3>
                    <p className="text-indigo-200 text-sm font-medium mb-4">{text.srvIndependientesDesc}</p>
                    <p className="text-gray-300 text-sm leading-relaxed font-[Poppins]">{text.srvIndependientesFull}</p>
                 </div>
              </div>

              <div className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-green-500 overflow-hidden cursor-pointer" onClick={() => navigate('/nomina')}>
                 <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-green-500 to-transparent"></div>
                 <div className="relative z-10">
                    <div className="w-14 h-14 bg-green-600 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg"><FileSpreadsheet size={28} /></div>
                    <h3 className="text-xl font-bold mb-2 text-white font-[Poppins] group-hover:text-green-300 transition-colors">{text.srvNomina}</h3>
                    <p className="text-green-200 text-sm font-medium mb-4">{text.srvNominaDesc}</p>
                    <p className="text-gray-300 text-sm leading-relaxed font-[Poppins]">{text.srvNominaFull}</p>
                 </div>
              </div>
            </div>
          </div>
      </section>

      {/* --- 5. ZONA DE EMPLEADOS --- */}
      <section className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
             <div className="bg-white rounded-[40px] p-10 md:p-14 shadow-2xl border border-gray-100 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 group">
                <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-purple-600 to-cyan-500"></div>
                <div className="text-left md:w-2/3">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-100 p-2 rounded-lg text-purple-600"><LogIn size={24} /></div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">{text.bukTitle}</h2>
                    </div>
                    <p className="text-gray-600 text-lg leading-relaxed mb-4">{text.bukDesc}</p>
                    <div className="flex items-center gap-2 mt-4 opacity-70 hover:opacity-100 transition-opacity">
                        <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{text.bukLabel}</span>
                        <img src="https://zaphy.com.co/wp-content/uploads/2026/01/logo_buk.png" alt="Buk Logo" className="h-8 w-auto grayscale hover:grayscale-0 transition-all" onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML += '<span class="font-bold text-gray-600">Buk</span>'; }} />
                    </div>
                </div>
                <div className="md:w-1/3 flex justify-center md:justify-end w-full">
                    <a href="https://zaphy.buk.co/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center gap-2 w-full md:w-auto justify-center">{text.bukBtn} <ArrowRight size={20} /></a>
                </div>
             </div>
          </div>
      </section>

      {/* --- SECCIÓN 7: TESTIMONIOS --- */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-100 rounded-full blur-[80px] opacity-60 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-100 rounded-full blur-[80px] opacity-60 pointer-events-none"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-extrabold text-gray-900 font-[Poppins] mb-4">{text.testTitle}</h2>
              <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                  <div className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-default">
                    <div className="flex items-center gap-1.5">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        <span className="font-bold text-gray-900 font-[Poppins] text-lg">{globalRating}</span>
                    </div>
                    <div className="h-4 w-px bg-gray-300 mx-1"></div>
                    <div className="flex text-yellow-400 gap-0.5">
                        <Star size={18} fill="currentColor" className="text-yellow-400" />
                        <Star size={18} fill="currentColor" className="text-yellow-400" />
                        <Star size={18} fill="currentColor" className="text-yellow-400" />
                        <Star size={18} fill="currentColor" className="text-yellow-400" />
                        <Star size={18} fill="currentColor" className={globalRating >= 4.8 ? "text-yellow-400" : "text-gray-300"} />
                    </div>
                  </div>
                  <span className="text-gray-500 font-medium font-[Poppins] text-sm bg-white/50 px-3 py-1 rounded-full border border-gray-100">
                      Basado en <strong>{totalReviews} reseñas</strong>
                  </span>
              </div>
            </div>
            
            <div className="relative group/carousel">
                <button onClick={() => scrollReviews('left')} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 z-20 bg-white hover:bg-purple-50 text-gray-700 hover:text-purple-600 p-3 rounded-full shadow-lg border border-gray-100 transition-all opacity-0 group-hover/carousel:opacity-100 focus:opacity-100" aria-label="Anterior"><ChevronLeft size={24} /></button>

                <div ref={reviewsContainerRef} className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide scroll-smooth" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {reviews.map((review, index) => (
                        <div key={review.id || index} className="snap-center shrink-0 w-[85vw] sm:w-[350px] md:w-[380px] bg-white p-8 rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 flex flex-col relative">
                             <div className="absolute top-6 right-8 text-purple-100"><Quote size={40} fill="currentColor" /></div>
                             <div className="flex items-center gap-4 mb-6">
                                {review.profile_photo_url ? (
                                    <img src={review.profile_photo_url} alt={review.author_name} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" referrerPolicy="no-referrer" />
                                ) : (
                                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-xl font-[Poppins] shadow-sm">{review.initial}</div>
                                )}
                                <div>
                                    <h4 className="font-bold text-gray-900 text-base font-[Poppins] line-clamp-1">{review.author_name}</h4>
                                    <div className="flex items-center gap-2">
                                        <div className="flex text-yellow-400 gap-0.5">
                                            {[...Array(review.rating || 5)].map((_, i) => ( <Star key={i} size={12} fill="currentColor" /> ))}
                                        </div>
                                        <span className="text-gray-400 text-xs font-[Poppins]">{review.relative_time_description}</span>
                                    </div>
                                </div>
                             </div>
                             <p className="text-gray-600 text-sm leading-relaxed font-[Poppins] flex-grow italic">"{review.text}"</p>
                             <div className="mt-6 pt-4 border-t border-gray-50 flex justify-between items-center">
                                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Verificado</span>
                                <div className="bg-gray-50 p-1.5 rounded-full"><img src="https://fonts.gstatic.com/s/i/productlogos/googleg/v6/24px.svg" alt="Google" className="w-4 h-4" /></div>
                             </div>
                        </div>
                    ))}
                </div>

                <button onClick={() => scrollReviews('right')} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 z-20 bg-white hover:bg-purple-50 text-gray-700 hover:text-purple-600 p-3 rounded-full shadow-lg border border-gray-100 transition-all opacity-0 group-hover/carousel:opacity-100 focus:opacity-100" aria-label="Siguiente"><ChevronRight size={24} /></button>
            </div>
            
            <div className="text-center mt-8">
               <p className="text-xs text-gray-400 mb-2 font-[Poppins]">{loadingReviews ? "Cargando reseñas..." : "Transparencia total: opiniones reales sincronizadas con Google."}</p>
               <a href={`https://search.google.com/local/reviews?placeid=${PLACE_ID}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-purple-600 font-bold text-sm hover:text-purple-800 transition-colors font-[Poppins] border-b-2 border-transparent hover:border-purple-200">Ver todas las reseñas en Google Maps <ArrowRight size={14} /></a>
            </div>
          </div>
      </section>

      {/* --- SECCIÓN 8: LLAMADO A LA ACCIÓN A WHATSAPP --- */}
      <section className="relative my-12 mx-4 sm:mx-6 lg:mx-8 overflow-hidden rounded-[32px] bg-white border border-gray-100 shadow-xl">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[150px] bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
        <div className="absolute -top-10 -right-10 text-emerald-500/5 pointer-events-none animate-[spin_40s_linear_infinite]">
          <Settings size={140} strokeWidth={1} fill="currentColor" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-10 sm:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-3 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full">
                <MessageCircle size={14} className="text-emerald-600 animate-pulse" />
                <span className="text-xs font-bold tracking-wider text-emerald-700 uppercase font-[Poppins]">
                  Respuesta Inmediata
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold font-[Poppins] leading-tight text-gray-900">
                {text.waTitle}
              </h3>
              <p className="text-gray-500 font-[Poppins] text-sm sm:text-base leading-relaxed max-w-xl mx-auto lg:mx-0">
                {text.waDesc}
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col items-center gap-4 bg-gray-50/80 backdrop-blur-md border border-gray-100 p-5 rounded-2xl w-full justify-center">
              <a 
                href="https://wa.me/573114044203?text=Hola%20Zaphy!%20Necesito%20asesor%C3%ADa%20con%20el%20%C3%A1rea%20de%20Talento%20Humano%20y%20SST%20de%20mi%20empresa."
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-slate-950 hover:bg-slate-900 text-white font-extrabold text-sm px-6 py-4 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3 font-[Poppins] w-full border border-slate-800"
              >
                <svg className="w-5 h-5 fill-[#25D366] group-hover:scale-110 transition-transform duration-300" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.513 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.713-1.457L0 24zm6.59-4.846c1.6.95 3.488 1.451 5.414 1.452 5.388 0 9.773-4.382 9.776-9.77a9.704 9.704 0 0 0-2.87-6.837 9.71 9.71 0 0 0-6.848-2.865c-5.39 0-9.778 4.383-9.781 9.772a9.67 9.67 0 0 0 1.474 5.074l-.993 3.626 3.716-.974zM17.486 14.37c-.3-.15-1.774-.875-2.026-.967-.253-.092-.437-.137-.62.137-.183.274-.707.875-.867 1.058-.16.183-.32.206-.62.056-.3-.15-1.267-.467-2.413-1.49-.893-.797-1.496-1.78-1.673-2.08-.177-.3-.02-.462.13-.61.135-.133.3-.347.45-.52.15-.173.2-.294.3-.49.1-.197.05-.368-.025-.52-.075-.15-.62-1.494-.85-2.043-.224-.54-.47-.466-.62-.474-.15-.008-.314-.01-.475-.01-.16 0-.418.06-.637.3-.218.238-.83.812-.83 1.98 0 1.17.85 2.296.97 2.455.12.158 1.67 2.548 4.044 3.57.565.243 1.007.388 1.352.498.568.18 1.083.154 1.49.094.453-.066 1.774-.724 2.026-1.42.253-.695.253-1.293.177-1.42-.076-.127-.253-.173-.553-.324z"/>
                </svg>
                {text.waBtn}
              </a>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;